<script setup>
/**
 * FinalCTA — full-width dark band before the contact form.
 *
 * v2: replaces the polite card-with-shadow with a cinematic dark band
 * (Hubstaff / Insightful pattern). Full bleed, ambient cyan halo, subtle
 * grid texture and orbiting accent dots for movement without distraction.
 */
import { useI18n } from "vue-i18n";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";

const { t } = useI18n();
</script>

<template>
  <section class="relative py-24 lg:py-32 bg-inverse text-inverse-foreground overflow-hidden">
    <!-- Subtle grid texture for depth -->
    <div
      class="absolute inset-0 opacity-[0.05]"
      :style="{
        backgroundImage: 'linear-gradient(hsl(var(--inverse-accent)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--inverse-accent)) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
      }"
    />

    <!-- Ambient cyan halo behind text -->
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-inverse-accent/8 rounded-full blur-3xl" />

    <!-- Decorative orbiting dots (purely visual) -->
    <div class="absolute top-10 left-10 w-1 h-1 rounded-full bg-inverse-accent/60 animate-pulse" />
    <div
      class="absolute bottom-16 right-16 w-1 h-1 rounded-full bg-inverse-accent/60 animate-pulse"
      style="animation-delay: 1.5s"
    />
    <div
      class="absolute top-1/3 right-1/4 w-1.5 h-1.5 rounded-full bg-inverse-accent/40 animate-pulse"
      style="animation-delay: 0.8s"
    />

    <div class="container relative z-10 mx-auto px-6">
      <AnimatedSection animation="fade-up" class="max-w-3xl mx-auto text-center">
        <p class="text-xs font-semibold uppercase tracking-widest text-inverse-accent mb-4">
          {{ t("landing.final_cta.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-5 tracking-tight leading-[1.1]">
          {{ t("landing.final_cta.title") }}
        </h2>
        <p class="text-lg text-inverse-muted-foreground mb-10 leading-relaxed max-w-2xl mx-auto">
          {{ t("landing.final_cta.subtitle") }}
        </p>
        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#contact-form">
            <Button variant="hero-primary" size="xl" class="w-full sm:w-auto">
              {{ t("landing.final_cta.primary") }}
              <ArrowRight class="w-4 h-4" />
            </Button>
          </a>
          <a href="#how-it-works">
            <Button
              variant="ghost"
              size="xl"
              class="w-full sm:w-auto text-inverse-foreground hover:bg-inverse-border/40 hover:text-inverse-foreground"
            >
              {{ t("landing.final_cta.secondary") }}
            </Button>
          </a>
        </div>

        <!-- Trust micro-row matching the hero, restated at the moment of decision -->
        <ul class="flex flex-wrap justify-center gap-x-5 gap-y-2 mt-10 text-xs text-inverse-muted-foreground">
          <li class="flex items-center gap-1.5">
            <span class="inline-block w-1.5 h-1.5 rounded-full bg-inverse-accent" />
            {{ t("landing.hero.trust.0") }}
          </li>
          <li class="flex items-center gap-1.5">
            <span class="inline-block w-1.5 h-1.5 rounded-full bg-inverse-accent" />
            {{ t("landing.hero.trust.1") }}
          </li>
          <li class="flex items-center gap-1.5">
            <span class="inline-block w-1.5 h-1.5 rounded-full bg-inverse-accent" />
            {{ t("landing.hero.trust.2") }}
          </li>
        </ul>
      </AnimatedSection>
    </div>
  </section>
</template>
